import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { cn } from "@loot-os/ui/lib/utils";
import type { ReactNode } from "react";

/**
 * One titled card on a detail page: a small heading, an optional control at its right, and what
 * the card holds — usually a `FactList`. The heading row keeps the height of a small button
 * whether or not a control is present, so a control that appears on edit (the notes' Save)
 * never pushes the body down.
 */
export function FactGroup({
  title,
  action,
  className,
  children,
}: {
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card size="sm" className={cn("min-w-0", className)}>
      <CardHeader className="min-h-8 items-center">
        <CardTitle>{title}</CardTitle>
        {action && <CardAction className="self-center">{action}</CardAction>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** Label/value pairs, one per row, the labels in a column of their own. */
export function FactList({ children }: { children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
      {children}
    </dl>
  );
}

/** One row of a `FactList`; `mono` for ids and amounts. Long values wrap rather than truncate. */
export function Fact({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("min-w-0 break-words", mono && "font-mono text-xs leading-5")}>
        {children}
      </dd>
    </>
  );
}

/** A headline figure: the label small above, the value large beneath. */
export function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="truncate text-lg font-semibold tabular-nums">{children}</span>
    </div>
  );
}
